/**
 * feat-338 — Section 2, the Questions table (design 01/02, NOTES §4).
 *
 * Frozen question column, client re-sort (not-enough-data and suppressed rows always
 * last; blanks last both ways), one expanded row at a time. The expanded panel is a
 * full-width row whose inner div is `sticky left-0` and exactly the scroll
 * container's visible width (`--qa-viewport`, kept current by a ResizeObserver), so
 * it never scrolls sideways with the table.
 */
import React, { useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight, Copy, Pencil } from 'lucide-react';
import type { QuestionRow, QuestionsMeta } from '../../../../services/questionAnalysisService';
import {
  C,
  DEFAULT_TIMEZONE,
  FLAG,
  QA_COPY,
  QUESTION_COLUMNS,
  QuestionSort,
  QuestionSortKey,
  TYPE_LABEL,
  formatAnswerSeconds,
  formatShortDate,
  pct,
  pctTitle,
} from './questionAnalysisFormat';
import { NullDash, SuppressedValue, countOrFewer, orDash, useSuppressFloor } from './parts';

export const QUESTION_COL_COUNT = 11;

/** The expanded panel's width: the scroll container's visible width (NOTES §4). */
export const DETAIL_PANEL_STYLE: React.CSSProperties = { width: 'var(--qa-viewport, 100%)' };

interface Props {
  rows: QuestionRow[];
  meta: QuestionsMeta;
  sort: QuestionSort;
  onSort: (k: QuestionSortKey) => void;
  expandedId: string | null;
  onToggle: (id: string) => void;
  renderDetail: (row: QuestionRow) => React.ReactNode;
}

const FROZEN_W = 'w-[240px] min-w-[240px] max-w-[240px] md:w-[340px] md:min-w-[340px] md:max-w-[340px]';

const SortTh: React.FC<{
  label: string[];
  title: string;
  minW: string;
  active: boolean;
  dir: 'asc' | 'desc';
  onClick: () => void;
  testId: string;
}> = ({ label, title, minW, active, dir, onClick, testId }) => (
  <th
    scope="col"
    aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    className={`${C.th} ${minW} text-right`}
  >
    <button
      type="button"
      data-testid={testId}
      title={title}
      onClick={onClick}
      className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide whitespace-nowrap text-right ${
        active ? 'text-ink' : 'text-inkMuted hover:text-ink'
      }`}
    >
      <span>
        {label.map((l, i) => (
          <React.Fragment key={l}>
            {i > 0 && <br />}
            {l}
          </React.Fragment>
        ))}
      </span>
      {active && (
        <span aria-hidden="true" className="text-[10px]">
          {dir === 'desc' ? '▼' : '▲'}
        </span>
      )}
    </button>
  </th>
);

const FlagsCell: React.FC<{ row: QuestionRow; meta: QuestionsMeta }> = ({ row, meta }) => {
  const t = meta.thresholds;
  if (!row.enough_data && !row.suppressed) {
    return (
      <>
        <span className={C.notEnough} title={QA_COPY.notEnoughTitle(t.min_learners_to_flag)} data-testid="not-enough">
          {QA_COPY.notEnoughData}
        </span>
        <span className={C.flagRule}>{QA_COPY.learnersNeeded(row.learners, t.min_learners_to_flag)}</span>
      </>
    );
  }
  if (!row.flags.length) return <span className="text-inkMuted text-xs">{QA_COPY.noFlags}</span>;
  return (
    <>
      {row.flags.map((f) => {
        const def = FLAG[f];
        return (
          <div key={f} className="mb-1.5 last:mb-0" data-testid={`flag-${f}`}>
            <span className={C.flagChip}>
              <def.Icon className="w-3 h-3" aria-hidden="true" />
              {def.label}
            </span>
            <span className={C.flagRule}>{def.rule(t)}</span>
          </div>
        );
      })}
    </>
  );
};

const NA: React.FC = () => <span className="sr-only">{QA_COPY.notApplicableMc}</span>;

export const EditedBadge: React.FC<{ iso: string; meta: QuestionsMeta }> = ({ iso, meta }) => {
  const d = formatShortDate(iso, meta.range.timezone || DEFAULT_TIMEZONE);
  return (
    <span
      className={C.editedChip}
      data-testid="edited-badge"
      title={meta.include_earlier_versions ? QA_COPY.editedTitleAll(d) : QA_COPY.editedTitle(d, false)}
    >
      <Pencil className="w-3 h-3" aria-hidden="true" />
      {QA_COPY.edited(d)}
    </span>
  );
};

const QuestionRowView: React.FC<{
  row: QuestionRow;
  meta: QuestionsMeta;
  expanded: boolean;
  onToggle: () => void;
}> = ({ row, meta, expanded, onToggle }) => {
  const muted = row.suppressed || !row.enough_data;
  const floor = useSuppressFloor();
  const bg = expanded ? 'bg-cream' : 'bg-white group-hover:bg-cream';
  const toggleOnKey = (e: React.MouseEvent) => {
    // Let the chevron button handle its own click; the row click toggles too.
    if ((e.target as HTMLElement).closest('button')) return;
    onToggle();
  };
  const frozen = (
    <th scope="row" className={`${C.tdFrozen} ${bg} ${FROZEN_W} text-left font-normal`}>
      <div className="flex items-start gap-2">
        <button
          type="button"
          aria-expanded={expanded}
          aria-label={expanded ? QA_COPY.collapse : QA_COPY.expand}
          data-testid="row-toggle"
          onClick={onToggle}
          className="mt-0.5 shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-inkMuted hover:bg-cream-dark hover:text-ink"
        >
          {expanded ? <ChevronDown className="w-4 h-4" aria-hidden="true" /> : <ChevronRight className="w-4 h-4" aria-hidden="true" />}
        </button>
        <div className="min-w-0">
          <p
            className={`text-sm font-semibold ${muted ? 'text-inkMuted' : 'text-ink'} leading-snug line-clamp-2`}
            title={row.question_text}
            data-testid="question-text"
          >
            {row.question_text}
          </p>
          <p className="text-xs text-inkMuted mt-0.5 truncate" title={`${row.drill_name} · ${row.lesson_name}`}>
            {row.drill_name} · {row.lesson_name}
          </p>
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            <span className={C.typeChip}>{TYPE_LABEL[row.question_type]}</span>
            {row.edited_at && <EditedBadge iso={row.edited_at} meta={meta} />}
            {/* feat-354 — last in the chip line; suppressed rows too (a curriculum fact). */}
            {(row.copies ?? 0) > 0 && (
              <span className={C.copiesChip} title={QA_COPY.copiesTitle(row.copies!)} data-testid="copies-chip">
                <Copy className="w-3 h-3" aria-hidden="true" />
                {QA_COPY.copies(row.copies!)}
              </span>
            )}
          </div>
          {!row.suppressed && (
            <p className={`md:hidden text-xs mt-1 ${muted ? 'text-inkMuted' : 'text-ink'}`} data-testid="mobile-line">
              {QA_COPY.mobileLine(pct(row.first_try_wrong_rate), row.flags.length, !row.enough_data)}
            </p>
          )}
        </div>
      </div>
    </th>
  );

  if (row.suppressed) {
    return (
      <tr
        className={`group text-inkMuted cursor-pointer ${expanded ? '' : 'hover:bg-cream'}`}
        data-testid="question-row"
        data-question-id={row.question_id}
        data-suppressed="true"
        onClick={toggleOnKey}
      >
        {frozen}
        <td colSpan={QUESTION_COL_COUNT - 1} className={`${C.td} text-sm`}>
          <SuppressedValue inline />
        </td>
      </tr>
    );
  }

  const na = row.fitb == null;
  return (
    <tr
      className={`group ${muted ? 'text-inkMuted' : 'text-ink'} cursor-pointer ${expanded ? '' : 'hover:bg-cream'}`}
      data-testid="question-row"
      data-question-id={row.question_id}
      data-enough-data={row.enough_data ? 'true' : 'false'}
      onClick={toggleOnKey}
    >
      {frozen}
      <td className={`${C.td} min-w-[230px]`}>
        <FlagsCell row={row} meta={meta} />
      </td>
      <td className={`${C.tdNum} min-w-[84px] text-sm`}>{row.learners}</td>
      <td className={`${C.tdNum} min-w-[84px] text-sm`}>{row.attempts}</td>
      <td
        className={`${C.tdNum} min-w-[104px] text-sm ${muted ? '' : 'font-bold'}`}
        title={pctTitle(row.first_try_wrong_rate)}
        data-testid="cell-wrong"
      >
        {orDash(pct(row.first_try_wrong_rate))}
      </td>
      <td className={`${C.tdNum} min-w-[112px] text-sm`} title={pctTitle(row.still_wrong_after_retry_rate)}>
        {row.still_wrong_after_retry_rate == null ? (
          <NullDash title={QA_COPY.nobodyRetried} />
        ) : (
          pct(row.still_wrong_after_retry_rate)
        )}
      </td>
      <td className={`${C.tdNum} min-w-[96px] text-sm`}>{orDash(formatAnswerSeconds(row.median_answer_ms))}</td>
      <td className={`${C.tdNum} min-w-[104px] text-sm`}>
        {orDash(formatAnswerSeconds(row.p90_answer_ms))}
        {row.capped_count != null && row.capped_count > 0 && (
          <span className="block text-[11px] text-inkMuted" title={QA_COPY.cappedTitle(row.capped_count)}>
            {QA_COPY.cappedLeftOut(row.capped_count)}
          </span>
        )}
      </td>
      <td className={`${C.tdNum} min-w-[84px] text-sm`} title={pctTitle(row.exit_rate)}>
        {orDash(pct(row.exit_rate))}
      </td>
      <td className={`${C.tdNum} min-w-[92px] text-sm`} data-testid="cell-ai-checked">
        {na ? <NA /> : countOrFewer(row.fitb!.ai_graded_learners, floor)}
      </td>
      <td className={`${C.tdNum} min-w-[92px] text-sm`} data-testid="cell-ai-rejected">
        {na ? <NA /> : countOrFewer(row.fitb!.rejected_learners, floor)}
      </td>
    </tr>
  );
};

const QuestionsTable: React.FC<Props> = ({ rows, meta, sort, onSort, expandedId, onToggle, renderDetail }) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Keep --qa-viewport equal to the scroll container's visible width (NOTES §4).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const set = () => el.style.setProperty('--qa-viewport', `${el.clientWidth}px`);
    set();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const isDefault = sort.key === 'default';
  return (
    <div className="min-w-0">
      <div className={C.scroll} ref={scrollRef} data-testid="questions-scroll">
        <table className={C.table} data-testid="questions-table">
          <thead>
            <tr>
              <th scope="col" className={`${C.thFrozen} ${FROZEN_W} text-inkMuted`}>
                Question
              </th>
              <th scope="col" aria-sort={isDefault ? 'descending' : 'none'} className={`${C.th} text-left text-inkMuted`}>
                <button
                  type="button"
                  data-testid="sort-default"
                  title={QA_COPY.defaultOrderTitle}
                  onClick={() => onSort('default')}
                  className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide ${
                    isDefault ? 'text-ink' : 'text-inkMuted hover:text-ink'
                  }`}
                >
                  Flags
                  {isDefault && (
                    <span aria-hidden="true" className="text-[10px]">
                      ▼
                    </span>
                  )}
                </button>
              </th>
              {QUESTION_COLUMNS.map((c) => (
                <SortTh
                  key={c.key}
                  label={c.label}
                  title={c.title}
                  minW={c.minW}
                  active={sort.key === c.key}
                  dir={sort.dir}
                  onClick={() => onSort(c.key)}
                  testId={`sort-${c.key}`}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const open = expandedId === r.question_id;
              return (
                <React.Fragment key={r.question_id}>
                  <QuestionRowView row={r} meta={meta} expanded={open} onToggle={() => onToggle(r.question_id)} />
                  {open && (
                    <tr data-testid="detail-row">
                      <td colSpan={QUESTION_COL_COUNT} className="p-0 border-t border-softBorder bg-cream">
                        <div
                          data-testid="detail-panel"
                          className="sticky left-0 p-4 md:p-5 space-y-5"
                          style={DETAIL_PANEL_STYLE}
                        >
                          {renderDetail(r)}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default QuestionsTable;
